/**
 * Theme Configuration
 * ===================
 * 
 * Centralized color tokens and theme definitions for MacroMeal.
 * 
 * USAGE:
 * - CSS variables are defined in index.css and applied via [data-theme] attribute
 * - Components should use CSS variables: var(--color-primary) instead of hardcoded values
 * - For Tailwind: Use the extended theme colors defined in tailwind.config.js
 * 
 * ADDING NEW COLORS:
 * 1. Add the semantic name and values here
 * 2. Add CSS variable in index.css for both light and dark themes
 * 3. Optionally extend Tailwind config if needed
 */

// Available themes
export const THEMES = {
  LIGHT: 'light',
  DARK: 'dark',
  // SYSTEM: 'system', // Future: auto-detect from OS preference
};

export const DEFAULT_THEME = THEMES.LIGHT;

/**
 * Theme color tokens
 * 
 * Organized by semantic purpose rather than raw color names.
 * This allows themes to have different actual colors while maintaining consistent semantics.
 */
export const themeTokens = {
  light: {
    // Brand colors
    primary: '#2D4F1E',        // Dark green - main brand color
    primaryHover: '#1F3814',   // Darker green for hover states
    secondary: '#4F772D',      // Medium green - secondary actions
    secondaryHover: '#3D5E22', // Darker for hover
    accent: '#90A955',         // Light green - accents

    // Background colors
    bgPrimary: '#FFFFFF',      // Main background
    bgSecondary: '#FAFAF9',    // Secondary/card backgrounds
    bgTertiary: '#F5F5F4',     // Tertiary/input backgrounds
    bgElevated: '#FFFFFF',     // Elevated surfaces (modals, dropdowns)

    // Text colors
    textPrimary: '#1C1917',    // Primary text (headings, important content)
    textSecondary: '#57534E',  // Secondary text (body, descriptions)
    textTertiary: '#78716C',   // Tertiary text (hints, placeholders)
    textMuted: '#A8A29E',      // Muted text (disabled, less important)
    textInverse: '#FFFFFF',    // Text on dark backgrounds

    // Border colors
    borderDefault: '#E7E5E4',  // Default borders
    borderHover: '#D6D3D1',    // Hover state borders
    borderFocus: '#2D4F1E',    // Focus state borders (uses primary)

    // Status colors (consistent across themes)
    success: '#4F772D',        // Green - success states
    successBg: '#F0FDF4',      // Success background
    warning: '#F59E0B',        // Amber - warning states
    warningBg: '#FFFBEB',      // Warning background
    error: '#BC4749',          // Red - error states
    errorBg: '#FEF2F2',        // Error background
    info: '#3B82F6',           // Blue - info states
    infoBg: '#EFF6FF',         // Info background

    // Macro colors (nutrition-specific)
    calories: '#F97316',       // Orange - calories
    protein: '#EF4444',        // Red - protein
    carbs: '#F59E0B',          // Amber - carbohydrates
    fat: '#3B82F6',            // Blue - fat

    // Shadows
    shadowSm: '0 1px 2px 0 rgb(0 0 0 / 0.05)',
    shadowMd: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)',
    shadowLg: '0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)',
  },

  dark: {
    // Brand colors (slightly adjusted for dark backgrounds)
    primary: '#4F772D',        // Medium green - more visible on dark
    primaryHover: '#5C8A34',   // Lighter for hover on dark
    secondary: '#90A955',      // Light green
    secondaryHover: '#A3BC68',
    accent: '#B5C78A',         // Even lighter accent

    // Background colors
    bgPrimary: '#1C1917',      // Main background (dark)
    bgSecondary: '#292524',    // Secondary backgrounds
    bgTertiary: '#44403C',     // Tertiary backgrounds
    bgElevated: '#312D2B',     // Elevated surfaces (cards, popovers)

    // Text colors
    textPrimary: '#FAFAF9',    // Primary text (light on dark)
    textSecondary: '#D6D3D1',  // Secondary text
    textTertiary: '#A8A29E',   // Tertiary text
    textMuted: '#918C86',      // Muted text (WCAG AA contrast ≥4.5:1)
    textInverse: '#1C1917',    // Text on light backgrounds

    // Border colors
    borderDefault: '#44403C',  // Default borders (darker)
    borderHover: '#57534E',    // Hover state
    borderFocus: '#4F772D',    // Focus state

    // Status colors (adjusted for dark mode visibility)
    success: '#6B9B37',        // Brighter green
    successBg: '#1A2E12',      // Dark green background
    warning: '#FBBF24',        // Brighter amber
    warningBg: '#422006',      // Dark amber background
    error: '#F87171',          // Brighter red
    errorBg: '#450A0A',        // Dark red background
    info: '#60A5FA',           // Brighter blue
    infoBg: '#1E3A5F',         // Dark blue background

    // Macro colors (brighter for dark mode)
    calories: '#FB923C',       // Brighter orange
    protein: '#F87171',        // Brighter red
    carbs: '#FBBF24',          // Brighter amber
    fat: '#60A5FA',            // Brighter blue

    // Shadows (more subtle on dark)
    shadowSm: '0 1px 2px 0 rgb(0 0 0 / 0.3)',
    shadowMd: '0 4px 6px -1px rgb(0 0 0 / 0.4), 0 2px 4px -2px rgb(0 0 0 / 0.3)',
    shadowLg: '0 10px 15px -3px rgb(0 0 0 / 0.5), 0 4px 6px -4px rgb(0 0 0 / 0.4)',
  },
};

/**
 * Get CSS custom properties for a theme
 * Used to generate the CSS variables
 */
export const getThemeCSSVariables = (theme) => {
  const tokens = themeTokens[theme];
  if (!tokens) return {};

  return {
    '--color-primary': tokens.primary,
    '--color-primary-hover': tokens.primaryHover,
    '--color-secondary': tokens.secondary,
    '--color-secondary-hover': tokens.secondaryHover,
    '--color-accent': tokens.accent,

    '--color-bg-primary': tokens.bgPrimary,
    '--color-bg-secondary': tokens.bgSecondary,
    '--color-bg-tertiary': tokens.bgTertiary,
    '--color-bg-elevated': tokens.bgElevated,

    '--color-text-primary': tokens.textPrimary,
    '--color-text-secondary': tokens.textSecondary,
    '--color-text-tertiary': tokens.textTertiary,
    '--color-text-muted': tokens.textMuted,
    '--color-text-inverse': tokens.textInverse,

    '--color-border-default': tokens.borderDefault,
    '--color-border-hover': tokens.borderHover,
    '--color-border-focus': tokens.borderFocus,

    '--color-success': tokens.success,
    '--color-success-bg': tokens.successBg,
    '--color-warning': tokens.warning,
    '--color-warning-bg': tokens.warningBg,
    '--color-error': tokens.error,
    '--color-error-bg': tokens.errorBg,
    '--color-info': tokens.info,
    '--color-info-bg': tokens.infoBg,

    '--color-calories': tokens.calories,
    '--color-protein': tokens.protein,
    '--color-carbs': tokens.carbs,
    '--color-fat': tokens.fat,

    '--shadow-sm': tokens.shadowSm,
    '--shadow-md': tokens.shadowMd,
    '--shadow-lg': tokens.shadowLg,
  };
};

export default themeTokens;
